import {
  anaestheticStats,
  edtStats,
  headlineStats,
  selectEntries,
  taggedEntryCount,
} from '@iculogbook/shared';
import type { AnaestheticCase, DateRange, EdtEntry, Entry } from '@iculogbook/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';

import { useAuth } from '../auth/AuthProvider.tsx';
import { Button, Card, Field, SegmentedControl, TextInput, useToast } from '../components/index.ts';
import { anaestheticRepository } from '../db/repositories/anaesthetic.ts';
import { casesRepository } from '../db/repositories/cases.ts';
import { communicationsRepository } from '../db/repositories/communications.ts';
import { edtRepository } from '../db/repositories/edt.ts';
import { proceduresRepository } from '../db/repositories/procedures.ts';
import { exportXlsx } from '../features/data/backup.ts';
import { fromDateTimeLocalValue } from '../features/log/datetime.ts';
import { generatePdf } from '../features/pdf/generate.ts';

/**
 * The ARCP summary options screen.
 *
 * **The GMC number lives in this component's state and nowhere else.** It is
 * typed here, passed to the generator, written into the PDF, and gone when this
 * screen unmounts. It is never written to Dexie, never sent to the server,
 * never logged. There is a test asserting it does not appear in the output when
 * the toggle is off, and the guard is re-checked in phase 20.
 */

/**
 * Four presets, not five. "Custom" used to be a fifth segment and did not fit
 * the card on a phone — the segments cannot shrink below their label width, so
 * the last one sat outside the card's right edge. It is now the pair of date
 * boxes below the control, which is what it always meant.
 */
const PRESETS = ['3m', '6m', '12m', 'all'] as const;
type Preset = (typeof PRESETS)[number];

/** A preset, or the dates the trainee typed. */
type Period = Preset | 'custom';

const PRESET_LABELS: Record<Preset, string> = {
  '3m': '3 months',
  '6m': '6 months',
  '12m': '12 months',
  all: 'All time',
};

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/** "1 case", "2 cases". Used only for the period summary line. */
function count(value: number, singular: string, plural = `${singular}s`): string {
  return `${String(value)} ${value === 1 ? singular : plural}`;
}

export function ReportRoute() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const userId = user?.id ?? null;

  const [now] = useState(() => new Date());
  const [preset, setPreset] = useState<Period>('12m');

  /*
   * Empty until used. The invariant this screen keeps is that these boxes hold
   * something if and only if the period is custom: typing a date switches to
   * custom, and choosing a preset clears them again. Anything else leaves a
   * date on screen that is not the period being reported, which is the kind of
   * quiet disagreement hazard H4 is about.
   */
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  function choosePreset(next: Preset): void {
    setPreset(next);
    setCustomFrom('');
    setCustomTo('');
  }

  const [includeGmc, setIncludeGmc] = useState(false);
  const [gmcNumber, setGmcNumber] = useState('');
  const [includeCurriculum, setIncludeCurriculum] = useState(true);
  const [includeListing, setIncludeListing] = useState(true);
  const [generating, setGenerating] = useState(false);

  const data = useLiveQuery(async () => {
    if (userId === null) {
      return {
        entries: [] as Entry[],
        edtEntries: [] as EdtEntry[],
        anaestheticCases: [] as AnaestheticCase[],
      };
    }

    const [cases, procedures, communications, edtEntries, anaestheticCases] = await Promise.all([
      casesRepository.list(userId),
      proceduresRepository.list(userId),
      communicationsRepository.list(userId),
      edtRepository.list(userId),
      anaestheticRepository.list(userId),
    ]);

    return {
      entries: [...cases, ...procedures, ...communications] as Entry[],
      edtEntries: edtEntries as EdtEntry[],
      anaestheticCases: anaestheticCases as AnaestheticCase[],
    };
  }, [userId]);

  const entries = data?.entries;
  const edtEntries = data?.edtEntries;
  const anaestheticCases = data?.anaestheticCases;

  const range = useMemo<DateRange>(() => {
    const to = endOfDay(now);

    // Every kind of entry, or "all time" would start after the trainee's
    // first teaching session and quietly drop it. Computed only where it is
    // needed — it is a pass over the whole logbook.
    const earliest = (): Date =>
      startOfDay(
        [
          ...(entries ?? []),
          ...(edtEntries ?? []),
          ...(anaestheticCases ?? []),
        ].reduce<Date | null>(
          (found, entry) =>
            found === null || entry.occurredAt.getTime() < found.getTime()
              ? entry.occurredAt
              : found,
          null,
        ) ?? now,
      );

    if (preset === 'custom') {
      /*
       * Either box may be left empty, and one date on its own is a sensible
       * half-open range rather than an error: a "from" alone means everything
       * since that date, a "to" alone means everything up to it.
       */
      const from = fromDateTimeLocalValue(`${customFrom}T00:00`) ?? earliest();
      const until = fromDateTimeLocalValue(`${customTo}T23:59`) ?? to;
      return { from, to: until };
    }

    if (preset === 'all') {
      return { from: earliest(), to };
    }

    const from = new Date(now);
    from.setMonth(from.getMonth() - (preset === '3m' ? 3 : preset === '6m' ? 6 : 12));
    return { from: startOfDay(from), to };
  }, [preset, customFrom, customTo, now, entries, edtEntries, anaestheticCases]);

  const inRange = useMemo(
    () => (entries === undefined ? [] : selectEntries(entries, range)),
    [entries, range],
  );

  const edtInRange = useMemo(
    () => (edtEntries === undefined ? [] : selectEntries(edtEntries, range)),
    [edtEntries, range],
  );

  const anaestheticInRange = useMemo(
    () => (anaestheticCases === undefined ? [] : selectEntries(anaestheticCases, range)),
    [anaestheticCases, range],
  );

  const stats = useMemo(() => headlineStats(inRange, range), [inRange, range]);
  const edt = useMemo(() => edtStats(edtInRange, range), [edtInRange, range]);
  const anaes = useMemo(
    () => anaestheticStats(anaestheticInRange, range),
    [anaestheticInRange, range],
  );
  const tagged = useMemo(
    () => taggedEntryCount([...inRange, ...edtInRange, ...anaestheticInRange], range),
    [inRange, edtInRange, anaestheticInRange, range],
  );

  /** Nothing to report on. A period of only EDT or anaesthetics is still worth reporting. */
  const nothingInRange = stats.total + edt.total + anaes.total === 0;

  /*
   * Two date boxes that are always available make this easy to do by accident,
   * and "no entries in this period" would send someone looking for the wrong
   * problem. Reported as itself instead.
   */
  const backToFront = range.from.getTime() > range.to.getTime();

  async function generate(): Promise<void> {
    if (user === null || nothingInRange) return;

    setGenerating(true);
    try {
      const { filename } = await generatePdf(inRange, {
        displayName: user.displayName,
        // Only passed when the trainee asked for it. Nothing else ever sees it.
        ...(includeGmc && gmcNumber.trim() !== '' ? { gmcNumber: gmcNumber.trim() } : {}),
        edtEntries: edtInRange,
        anaestheticCases: anaestheticInRange,
        range,
        includeCurriculum: includeCurriculum && tagged > 0,
        includeListing,
        generatedAt: new Date(),
      });

      showToast({ message: `${filename} downloaded.`, tone: 'success' });
    } catch {
      showToast({
        message: 'The PDF could not be generated. Try a shorter period.',
        tone: 'danger',
      });
    } finally {
      setGenerating(false);
    }
  }

  /**
   * The same period as the PDF, as a workbook. Synchronous and without a
   * loading state, unlike the PDF: there is no typesetting engine to fetch
   * and no fonts to load, so the file is built and handed to the browser in
   * a few milliseconds and a spinner would only flicker.
   */
  function downloadWorkbook(): void {
    if (user === null || nothingInRange) return;

    try {
      const filename = exportXlsx({
        displayName: user.displayName,
        range,
        entries: inRange,
        edtEntries: edtInRange,
        anaestheticCases: anaestheticInRange,
      });

      showToast({ message: `${filename} downloaded.`, tone: 'success' });
    } catch {
      showToast({ message: 'The spreadsheet could not be created.', tone: 'danger' });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        The framing the Dashboard's "ARCP summary" card used to carry, moved
        here with the card on 2026-08-23 when this screen got its own tab.
        Without it the page opens on a period selector with no statement of
        what it is for.
      */}
      <Card title="ARCP summary">
        <p className="text-sm text-muted">
          A PDF in the format a panel expects — procedures by capability level, case mix,
          out-of-hours work, and the full listing. The same period is also available as an Excel
          workbook, for sorting and filtering yourself.
        </p>
      </Card>

      <Card title="Period">
        <SegmentedControl
          label="Period"
          // Four word labels; see the prop's own note.
          compact
          segments={PRESETS.map((key) => ({ value: key, label: PRESET_LABELS[key] }))}
          // Nothing is highlighted once dates are typed: the period is then
          // whatever the boxes below say, and showing a preset as selected
          // would contradict them.
          value={preset === 'custom' ? null : preset}
          onChange={(next) => choosePreset(next)}
        />

        <p className="pt-4 text-xs text-faint">Or set your own dates</p>

        {/*
          Stacked on a phone, side by side only from the rail breakpoint up.
          Two native date inputs in a row overflowed the card on a real
          iPhone — reported twice — and neither Chromium nor Playwright's
          WebKit reproduced it at the same width, so the intrinsic width iOS
          gives `input[type=date]` is not something this can be laid out
          against. One full-width input per row has no such failure mode, and
          by 900px there is room for both several times over.
        */}
        <div className="flex flex-col gap-3 pt-1 rail:flex-row">
          <Field label="From" className="min-w-0 rail:flex-1">
            <TextInput
              type="date"
              value={customFrom}
              onChange={(event) => {
                setCustomFrom(event.target.value);
                setPreset('custom');
              }}
            />
          </Field>
          <Field label="To" className="min-w-0 rail:flex-1">
            <TextInput
              type="date"
              value={customTo}
              onChange={(event) => {
                setCustomTo(event.target.value);
                setPreset('custom');
              }}
            />
          </Field>
        </div>

        <p className="pt-3 text-sm text-muted">
          {/*
            "clinical" appears only when there is EDT to distinguish it from.
            A trainee who logs no EDT should read exactly the sentence they
            always have.
          */}
          {backToFront
            ? 'That period ends before it starts.'
            : nothingInRange
              ? 'No entries in this period.'
              : edt.total === 0 && anaes.total === 0
                ? `${count(stats.total, 'entry', 'entries')} — ${count(stats.procedures, 'procedure')} and ${count(stats.cases, 'case')}.`
                : [
                    `${count(stats.total, 'clinical entry', 'clinical entries')} — ${count(stats.procedures, 'procedure')} and ${count(stats.cases, 'case')}`,
                    ...(edt.total === 0 ? [] : [count(edt.total, 'EDT entry', 'EDT entries')]),
                    ...(anaes.total === 0
                      ? []
                      : [count(anaes.total, 'anaesthetic case', 'anaesthetic cases')]),
                  ].join(' · ') + '.'}
        </p>
      </Card>

      <Card title="What to include">
        <div className="flex flex-col gap-4">
          <Toggle
            label="My GMC number"
            checked={includeGmc}
            onChange={(next) => {
              setIncludeGmc(next);
              // Cleared the moment it is switched off, so it cannot be carried
              // into a later PDF by accident.
              if (!next) setGmcNumber('');
            }}
          />

          {!includeGmc ? null : (
            <Field
              label="GMC number"
              help="Entered for this PDF only. ICULogbook does not store your GMC number."
            >
              <TextInput
                value={gmcNumber}
                onChange={(event) => setGmcNumber(event.target.value)}
                inputMode="numeric"
                autoComplete="off"
                maxLength={20}
              />
            </Field>
          )}

          {tagged === 0 ? null : (
            <Toggle
              label="Curriculum mapping"
              hint={`${String(tagged)} entries carry a HiLLO tag`}
              checked={includeCurriculum}
              onChange={setIncludeCurriculum}
            />
          )}

          <Toggle
            label="Full entry listing"
            hint="The appendix — every entry, newest first"
            checked={includeListing}
            onChange={setIncludeListing}
          />
        </div>
      </Card>

      <div className="flex flex-col gap-2">
        <Button
          variant="primary"
          block
          className="h-14"
          loading={generating}
          disabled={nothingInRange}
          onClick={() => void generate()}
        >
          Generate PDF
        </Button>

        <Button
          variant="secondary"
          block
          disabled={nothingInRange}
          onClick={() => downloadWorkbook()}
        >
          Download as Excel
        </Button>
      </div>

      {nothingInRange ? null : (
        <p className="px-1 text-xs text-faint">
          {/*
            The toggles above are choices about how the PDF is laid out, and
            saying so matters most for the GMC number: it goes on the PDF's
            cover because that is the document handed over, and it is never
            written into the spreadsheet.
          */}
          Both cover the period chosen above. The options shape the PDF only — the spreadsheet is
          one sheet per kind of entry, and your GMC number is never written into it.
        </p>
      )}

      {!nothingInRange ? null : (
        <p className="px-1 text-sm text-muted">
          {backToFront
            ? 'The “from” date is after the “to” date. Swap them and the report will generate.'
            : 'There are no entries in the period you have chosen. Widen it above, or log something first.'}
        </p>
      )}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="flex flex-col">
        <span className="text-sm font-medium text-text">{label}</span>
        {hint === undefined ? null : <span className="text-xs text-faint">{hint}</span>}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className="grid h-touch w-16 shrink-0 place-items-center"
      >
        <span
          aria-hidden="true"
          className={
            checked
              ? 'flex h-7 w-12 items-center rounded-full bg-accent p-1'
              : 'flex h-7 w-12 items-center rounded-full border border-line-strong bg-surface-sunken p-1'
          }
        >
          <span
            className={
              checked
                ? 'ml-auto size-5 rounded-full bg-accent-contrast'
                : 'size-5 rounded-full bg-line-strong'
            }
          />
        </span>
      </button>
    </div>
  );
}
